import { defineStore } from 'pinia';
import { ref } from 'vue';
import { snapshotBreadcrumbs } from '@lumi/ui';
import { supportApi } from '../utils/api';

// Support-ticket domain types — mirror the support-api contract (TicketSummary /
// detail / messages). See support-api/src/tickets.
export type TicketStatus = 'open' | 'in_progress' | 'answer' | 'closed';
export type AuthorType = 'user' | 'support' | 'system_admin';

export const TICKET_STATUSES: readonly TicketStatus[] = ['open', 'in_progress', 'answer', 'closed'];

export interface ObjectRef {
  type: string;
  id: string;
}

export interface TicketSummary {
  id: string;
  tenantId: string;
  tenantSlug: string;
  moduleKey: string;
  subject: string;
  status: TicketStatus;
  createdByMasterUserId: string | null;
  createdByEmail: string | null;
  escalatedAt: string | null;
  answeredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TicketMessage {
  id: string;
  authorType: AuthorType;
  authorEmail: string | null;
  text: string;
  createdAt: string;
}

export interface TicketDetail extends TicketSummary {
  objectRefs: ObjectRef[];
  context: Record<string, unknown> | null;
  consentAt: string | null;
  consentVersion: string | null;
  escalationReason: string | null;
  messages: TicketMessage[];
}

// What the caller is allowed to do with the tenant's tickets, computed by the
// backend from the role (view_all vs view_own) and returned on both the list and
// the detail responses. Drives which controls the UI shows — no client-side
// role guessing.
export interface Capabilities {
  canViewAll: boolean;
  canAnswer: boolean;
  canEscalate: boolean;
  canClose: boolean;
}

const EMPTY_CAPABILITIES: Capabilities = {
  canViewAll: false,
  canAnswer: false,
  canEscalate: false,
  canClose: false,
};

export interface CreateTicketInput {
  subject: string;
  text: string;
  moduleKey?: string | null;
  objectRefs?: ObjectRef[];
  context?: Record<string, unknown> | null;
  consent: { version: string };
}

// The consent record attached to every ticket. One constant so the two surfaces
// that open a ticket cannot record different versions of the same consent.
export const TICKET_CONSENT_VERSION = 'v1';

/**
 * The body for opening a ticket, shared by the /support page and the sidebar's
 * quick dialog — the only part of ticket creation the two have in common. The
 * POST itself lives at each surface inside `useApiMutation` (issue 195, D5).
 *
 * The breadcrumb trail is snapshotted HERE, at body-build time, so it reflects
 * what the user did before pressing send, and it is only ever sent because the
 * consent checkbox each caller gates on was ticked.
 */
export function createTicketBody(subject: string, text: string): CreateTicketInput {
  return {
    subject,
    text,
    context: snapshotBreadcrumbs() as unknown as Record<string, unknown>,
    consent: { version: TICKET_CONSENT_VERSION },
  };
}

export const useSupportStore = defineStore('support', () => {
  const myTickets = ref<TicketSummary[]>([]);
  const current = ref<TicketDetail | null>(null);
  // The caller's capabilities for the active tenant/ticket, as returned by the
  // list and detail endpoints. Defaults to all-false until a response arrives.
  const capabilities = ref<Capabilities>({ ...EMPTY_CAPABILITIES });

  const loadingList = ref(false);
  const loadingDetail = ref(false);

  // One support client serves every tenant the user belongs to, so the tenant is
  // identified per-call via the header (mirrors the system-admin dual routing).
  // Exposed on the store because the WRITES no longer live here (issue 195, D5):
  // each of them is a user-initiated action whose pending state and failure
  // belong to the button that was pressed, so it is issued from the view inside
  // `useApiMutation`. What stays here is the shared state those writes produce.
  function tenantCfg(tenantSlug: string) {
    return { headers: { 'X-Tenant-Slug': tenantSlug } };
  }

  /**
   * Adopt a ticket detail returned by a write (create / reply / escalate /
   * status change). Every one of those endpoints answers with the full, updated
   * ticket, so the view hands it here rather than re-fetching.
   */
  function adoptTicket(detail: TicketDetail): TicketDetail {
    current.value = detail;
    return detail;
  }

  async function loadMyTickets(tenantSlug: string): Promise<void> {
    loadingList.value = true;
    try {
      // The backend scopes this list (all tenant tickets for a view_all caller,
      // own tickets otherwise) and returns the caller's capabilities alongside.
      const { data } = await supportApi.get('/tickets', tenantCfg(tenantSlug));
      myTickets.value = (data?.tickets ?? []) as TicketSummary[];
      capabilities.value = (data?.capabilities ?? { ...EMPTY_CAPABILITIES }) as Capabilities;
    } finally {
      loadingList.value = false;
    }
  }

  async function loadTicket(tenantSlug: string, id: string): Promise<TicketDetail> {
    loadingDetail.value = true;
    try {
      const { data } = await supportApi.get(`/tickets/${id}`, tenantCfg(tenantSlug));
      current.value = data as TicketDetail;
      capabilities.value = (data?.capabilities ?? { ...EMPTY_CAPABILITIES }) as Capabilities;
      return current.value;
    } finally {
      loadingDetail.value = false;
    }
  }

  return {
    myTickets,
    current,
    capabilities,
    loadingList,
    loadingDetail,
    tenantCfg,
    adoptTicket,
    loadMyTickets,
    loadTicket,
  };
});
